import {
  calculateDeliveryProposalOfferCargoStatus,
  calculateOfferCargoProcessStatus,
  isDeliveryProposalValid,
} from "@/features/offer-cargo-process-status";
import {
  calculateDeliveryProposalContractStatus,
  calculateOfferCargoProcessReceptionStatus,
} from "@/features/offer-cargo-process-status/badge-status";
import type {
  CarrierCargoTableData,
  CarrierCargoTableProps,
} from "@/pages/carrier-cargo-list/ui/CargoTable";
import { excludeNull } from "@/shared/lib/collection";
import {
  fromIso8601Date,
  fromIso8601DateTime,
  toLocaleTextAsDate,
  toLocaleTextAsDateTime,
} from "@/shared/lib/datetime";
import {
  type Loadable,
  transformLoadable,
  transformLoadables,
} from "@/shared/lib/loadable";
import type { FreightMatching } from "@x-mile/api-contract";
import { addDays } from "date-fns";

export const mapDraftOfferCargoToCarrierCargoTableData = (
  offerCargo: CarrierCargoTableProps["draftOfferCargos"]["data"][number],
  user: CarrierCargoTableProps["user"],
): CarrierCargoTableData => {
  const remarks: CarrierCargoTableData["remarks"] = [];
  // 24時間以内に作成された荷物はisNew
  if (fromIso8601DateTime(offerCargo.createdAt) > addDays(new Date(), -1)) {
    remarks.push("new");
  }
  // 自分のcompanyのdelivery_proposalがある荷物はisAlreadyProposed
  if (
    offerCargo.process.status === "success" &&
    offerCargo.process.data.deliveryProposalOfferCargos.some(
      (deliveryProposalOfferCargo) =>
        deliveryProposalOfferCargo.company?.id === user.companyId,
    )
  ) {
    remarks.push("already_proposed");
  }
  const firstDestination = offerCargo.destinations[0];
  const lastDestination =
    offerCargo.destinations[offerCargo.destinations.length - 1];
  const deadlineAtDate = fromIso8601DateTime(offerCargo.deadlineAt);
  // TODO: propsにofferCargoProcessを以下の型で渡すようmodel/fetchを修正する
  const offerCargoProcess = transformLoadable(offerCargo.process, (process) => {
    return {
      ...process,
      draftOfferCargo: { ...offerCargo, process },
      contractOfferCargos: excludeNull([
        process.contractProposalOfferCargo,
        process.contractConcludedOfferCargo,
      ]),
    };
  });
  const myLatestValidDeliveryOfferCargo: Loadable<
    FreightMatching.schemas.OfferCargo | undefined
  > = transformLoadable(offerCargoProcess, (process) => {
    return process.deliveryProposalOfferCargos
      .filter((deliveryProposal) => {
        return (
          deliveryProposal.company?.id === user.companyId &&
          isDeliveryProposalValid(process, deliveryProposal)
        );
      })
      .sort((a, b) => {
        return (
          fromIso8601DateTime(b.createdAt).getTime() -
          fromIso8601DateTime(a.createdAt).getTime()
        );
      })[0];
  });
  const offerCargoProcessStatus = transformLoadable(
    offerCargoProcess,
    (process) => {
      return calculateOfferCargoProcessStatus({
        process,
      });
    },
  );
  const receptionStatus = transformLoadable(
    offerCargoProcessStatus,
    (status) => {
      return calculateOfferCargoProcessReceptionStatus({
        viewer: "carrier",
        status,
      });
    },
  );
  const deliveryProposalOfferCargoStatus = transformLoadables(
    [offerCargoProcess, myLatestValidDeliveryOfferCargo],
    (process, deliveryProposalOfferCargo) => {
      if (!deliveryProposalOfferCargo) {
        return undefined;
      }
      return calculateDeliveryProposalOfferCargoStatus({
        process,
        deliveryProposalOfferCargoId: deliveryProposalOfferCargo.id,
      });
    },
  );
  const contractStatus = transformLoadable(
    deliveryProposalOfferCargoStatus,
    (status) => {
      if (!status) {
        return undefined;
      }
      return calculateDeliveryProposalContractStatus({
        viewer: "carrier",
        status,
      });
    },
  );
  return {
    id: offerCargo.id,
    remarks,
    departure: `${firstDestination.prefecture} ${firstDestination.address}`,
    loadingOn: toLocaleTextAsDate(fromIso8601Date(offerCargo.loadingOn)),
    destination: `${lastDestination.prefecture} ${lastDestination.address}`,
    unloadingOn: toLocaleTextAsDate(fromIso8601Date(offerCargo.unloadingOn)),
    transitCount: offerCargo.destinations.length - 2,
    shippingFee: offerCargo.shippingFee,
    name: offerCargo.name ?? "",
    packingStyle: offerCargo.packingStyle,
    receptionStatus,
    expiration: toLocaleTextAsDateTime(deadlineAtDate),
    contractStatus,
  };
};
